import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ThumbsUp } from "lucide-react";
import { useState } from "react";
import { FormProvider, useFormContext } from "react-hook-form";
import { z } from "zod";

export const orderDetailSchema = z.object({
  invoice_id: z.string().min(1, "Invoice ID is required"),
  invoice_date: z.string().min(1, "Invoice date is required"),
  amount: z
    .number({
      required_error: "Amount is required",
      invalid_type_error: "Amount is required",
    })
    .min(1, "Amount is required"),
  notes: z.string().optional(),
  invoice_file: z
    .any()
    .refine((file) => file instanceof File, {
      message: "Attach the invoice PDF",
    })
    .refine((file) => !(file instanceof File) || file.type === "application/pdf", {
      message: "The invoice must be a PDF",
    })
    .refine((file) => !(file instanceof File) || file.size <= 10 * 1024 * 1024, {
      message: "The invoice must be 10 MB or smaller",
    }),
});

export type OrderDetail = z.infer<typeof orderDetailSchema>;

interface OrderDetailFormProps {
  onSubmit: () => void;
  invoiceDetails?: any;
}

export default function OrderDetailForm({
  onSubmit,
  invoiceDetails,
}: OrderDetailFormProps) {
  const methods = useFormContext<OrderDetail>();
  const [open, setOpen] = useState(false);

  // Get the grand_total value
  const grandTotal = invoiceDetails?.generic_order?.grand_total;

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger>
          <button className="p-2 bg-green-50 rounded-lg w-20 h-[34px]flex items-center justify-center">
            <ThumbsUp
              className="text-green-500 w-6 h-6 sm:w-7 sm:h-7"
              size={24}
            />
          </button>
        </AlertDialogTrigger>
        <AlertDialogPortal>
          <AlertDialogOverlay className=" bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
          {/* Sized by its content up to the viewport, with only the body
              scrolling, so the scrollbar sits on the dialog's edge and the
              header and buttons stay in view. */}
          <AlertDialogContent className="bg-white-A700 rounded-lg p-0 gap-0 w-[calc(100%-2rem)] max-w-xl max-h-[90vh] flex flex-col overflow-hidden animate-contentShow">
            <AlertDialogHeader className="flex text-left border-b rounded-t py-5 px-8 sm:px-5 shrink-0">
              <AlertDialogTitle className="font-bold capitalize p-0 text-lg text-black-label">
                Order Details
              </AlertDialogTitle>
              <AlertDialogDescription className="font-medium p-0 text-sm text-[#7E7E7E]">
                Record the invoice raised for this lead.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <FormProvider {...methods}>
              <form
                className="flex-1 min-h-0 overflow-y-auto px-8 sm:px-5 py-6"
                onSubmit={(e) => e.preventDefault()}
              >
                <div className="grid grid-cols-2 sm:grid-cols-1 gap-x-4 gap-y-5">
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="invoice_id"
                      className="text-sm font-medium text-black-label"
                    >
                      Invoice ID
                    </label>
                    <input
                      {...methods.register("invoice_id", {
                        required: "Invoice ID is required",
                      })}
                      id="invoice_id"
                      name="invoice_id"
                      type="text"
                      placeholder="e.g. INV-2026-114"
                      className={`border ${
                        methods.formState.errors.invoice_id
                          ? "border-red-500"
                          : "border-[#C4C4C4]"
                      } rounded-lg px-4 h-12 w-full`}
                    />
                    {methods.formState.errors.invoice_id && (
                      <span className="text-red-500 text-sm">
                        {methods.formState.errors.invoice_id.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="invoice_date"
                      className="text-sm font-medium text-black-label"
                    >
                      Invoice date
                    </label>
                    <input
                      {...methods.register("invoice_date", {
                        required: "Invoice date is required",
                      })}
                      id="invoice_date"
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      name="invoice_date"
                      className={`border ${
                        methods.formState.errors.invoice_date
                          ? "border-red-500"
                          : "border-[#C4C4C4]"
                      } rounded-lg px-4 h-12 w-full`}
                    />
                    {methods.formState.errors.invoice_date && (
                      <span className="text-red-500 text-sm">
                        {methods.formState.errors.invoice_date.message}
                      </span>
                    )}
                  </div>

                  <div className="col-span-2 sm:col-span-1 flex flex-col gap-2">
                    <label
                      htmlFor="amount"
                      className="text-sm font-medium text-black-label"
                    >
                      Total amount
                    </label>
                    <div className="relative w-full">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                        <span className="font-medium leading-4 px-2.5 py-2 rounded-md bg-[#E1EBFC] text-blue-700 text-sm">
                          INR
                        </span>
                      </div>
                      <input
                        {...methods.register("amount", {
                          valueAsNumber: true,
                          required: "Amount is required",
                          min: {
                            value: 1,
                            message: "Amount must be greater than 0",
                          },
                          max: {
                            value:
                              Number(
                                invoiceDetails?.generic_order?.grand_total,
                              ) || Infinity,
                            message: `Amount should not exceed ${invoiceDetails?.generic_order?.grand_total}`,
                          },
                        })}
                        id="amount"
                        type="number"
                        name="amount"
                        step="0.01"
                        onWheel={(e) => e.currentTarget.blur()}
                        placeholder="Invoice total"
                        className={`border ${
                          methods.formState.errors.amount
                            ? "border-red-500"
                            : "border-[#C4C4C4]"
                        } h-12 w-full pl-[68px] pr-4 rounded-lg`}
                      />
                    </div>
                    {methods.formState.errors.amount && (
                      <span className="text-red-500 text-sm">
                        {methods.formState.errors.amount.message}
                      </span>
                    )}
                  </div>

                  <div className="col-span-2 sm:col-span-1 flex flex-col gap-2">
                    <span className="text-sm font-medium text-black-label">
                      Invoice (PDF)
                    </span>
                    {/* The whole row is the file input's label, so a click
                        anywhere on it, button included, opens the picker
                        natively. No script click: a hidden input clicked
                        from code does nothing inside these dialogs. */}
                    <label
                      htmlFor="invoice_file"
                      // A PDF can also be dropped on the row, for a browser
                      // that will not open a file picker from a dialog.
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "copy";
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          methods.setValue("invoice_file", file, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      className={`w-full h-12 pl-4 pr-1.5 flex justify-between items-center gap-3 border rounded-lg cursor-pointer focus-within:ring-2 focus-within:ring-blue-700 ${
                        methods.watch("invoice_file")
                          ? "bg-[#F2F7FF]"
                          : "bg-white-A700"
                      } ${
                        methods.formState.errors.invoice_file
                          ? "border-red-500"
                          : "border-[#C4C4C4]"
                      }`}
                    >
                      <span className="flex gap-3 items-center min-w-0">
                        <img
                          src="/images/remit/file.svg"
                          alt=""
                          className="w-5 h-5 shrink-0"
                        />
                        <span
                          className={`truncate text-sm ${
                            methods.watch("invoice_file")
                              ? "text-black-label"
                              : "text-[#7E7E7E]"
                          }`}
                        >
                          {methods.watch("invoice_file")?.name ||
                            "Choose or drop a PDF"}
                        </span>
                      </span>
                      <span className="shrink-0 px-4 py-2 rounded-md text-sm whitespace-nowrap text-blue-700 bg-white-A700 border border-blue-700">
                        {methods.watch("invoice_file") ? "Change" : "Upload"}
                      </span>
                      <input
                        id="invoice_file"
                        type="file"
                        aria-label="Upload invoice PDF"
                        accept="application/pdf,.pdf"
                        className="sr-only"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            methods.setValue("invoice_file", file, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                          // Lets the same file be picked again after a change.
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {methods.formState.errors.invoice_file ? (
                      <span className="text-red-500 text-sm">
                        {String(methods.formState.errors.invoice_file.message)}
                      </span>
                    ) : (
                      <span className="text-xs text-[#7E7E7E]">
                        PDF, up to 10 MB
                      </span>
                    )}
                  </div>

                  <div className="col-span-2 sm:col-span-1 flex flex-col gap-2">
                    <label
                      htmlFor="notes"
                      className="text-sm font-medium text-black-label"
                    >
                      Internal notes{" "}
                      <span className="font-normal text-[#7E7E7E]">
                        (optional)
                      </span>
                    </label>
                    <textarea
                      {...methods.register("notes")}
                      id="notes"
                      name="notes"
                      rows={3}
                      placeholder="Anything the desk should know"
                      className="border border-[#C4C4C4] rounded-lg px-4 py-3 w-full resize-none"
                    />
                  </div>
                </div>
              </form>
            </FormProvider>
            <AlertDialogFooter className="flex justify-end items-center gap-2.5 py-4 px-8 sm:px-5 flex-row rounded-b-lg border-t bg-gray-button-gray-lite shrink-0">
              <AlertDialogCancel
                className=" m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                asChild
              >
                <button
                  onClick={() => {
                    methods.reset();
                    methods.clearErrors();
                  }}
                >
                  Cancel
                </button>
              </AlertDialogCancel>
              <AlertDialogAction
                className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                asChild
              >
                <button
                  className="disabled:opacity-40"
                  disabled={
                    !methods.formState.isValid ||
                    methods.formState.isSubmitting
                  }
                  onClick={methods.handleSubmit(async () => {
                    try {
                      await onSubmit();
                    } catch (error) {
                      console.error(error);
                    }
                  })}
                >
                  Submit
                </button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>
    </>
  );
}
